import React, { useState } from 'react';
import {
    Drawer,
    List,
    ListItemButton,
    ListItemText,
    Toolbar,
    useMediaQuery,
    IconButton,
    Button,
    Menu,
    MenuItem,
    Box,
    Dialog,
    DialogContent,
    DialogActions,
    TextField
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SidebarProps } from '../../../interfaces/Message';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

const drawerWidth = 240;
const drawerHeight = '100%';

const Sidebar: React.FC<SidebarProps> = ({
    open, onClose, conversations, selectedId, onSelect, onNewChat, onEditChat, onDeleteChat
}) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const { t } = useTranslation();
    // Stati per i menu contestuali
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedChatId, setSelectedChatId] = useState<number | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
    const [editTitle, setEditTitle] = useState<string>('');

    // Gestione menu contestuale
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, chatId: number) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedChatId(chatId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedChatId(null);
    };

    // Gestione modifica
    const handleEditClick = () => {
        const conversation = conversations.find(conv => conv.id === selectedChatId);
        if (conversation) {
            setEditTitle(conversation.name || `Chat ${conversation.id}`);
            setEditDialogOpen(true);
            setAnchorEl(null);
        }
    };

    const handleEditConfirm = () => {
        if (selectedChatId && editTitle.trim()) {
            onEditChat(selectedChatId, editTitle.trim());
            setEditDialogOpen(false);
            setEditTitle('');
            setSelectedChatId(null);
        }
    };

    // Gestione cancellazione
    const handleDeleteClick = () => {
        if (selectedChatId) {
            onDeleteChat(selectedChatId);
        }
        handleMenuClose();
    };

    return (
        <Drawer
            variant={isDesktop ? 'persistent' : 'temporary'}
            open={open}
            onClose={onClose}
            sx={{
                '& .MuiDrawer-paper': {
                    width: isDesktop ? drawerWidth : '100%',
                    height: isDesktop ? drawerHeight : '100%',
                    boxSizing: 'border-box',
                    top: isDesktop ? 67 : 0,
                    padding: 2,
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            {/* Header fisso */}
            <Toolbar
                sx={{
                    '&.MuiToolbar-root': {
                        padding: '1rem 0',
                        width: '100%',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '1.3rem',
                        fontWeight: 'bold',
                        flexShrink: 0
                    }
                }}
            >
                <h2>Conversazioni</h2>
                {!isDesktop && (
                    <IconButton onClick={onClose}>
                        <ClearIcon />
                    </IconButton>
                )}
            </Toolbar>

            {/* Lista delle conversazioni con altezza fissa e scroll */}
            <Box sx={{
                flex: 1,
                height: 'calc(100vh - 100px)',
                overflow: 'auto',
                minHeight: 0,
                mb: isDesktop ? 11 : 0,
                pb: 0
            }}>
                <List className='pt-0 pb-0 h-auto'>
                    {(conversations).map((conversation) => {
                        return (
                            <ListItemButton
                                key={conversation.id}
                                selected={conversation.id === selectedId}
                                onClick={() => {
                                    onSelect(conversation.id);
                                    if (!isDesktop) onClose();
                                }}
                                sx={{
                                    height: '3rem',
                                    borderRadius: 2,
                                    mb: 1,
                                    padding: 2,
                                    '&.Mui-selected': {
                                        backgroundColor: '#E9495A',
                                        color: '#fff',
                                        '&:hover': {
                                            backgroundColor: "#E6263B",
                                        }
                                    },
                                    '&:hover .chat-menu-icon': {
                                        opacity: 1,
                                    }
                                }}
                            >
                                <ListItemText
                                    primary={conversation.name || `Chat ${conversation.id}`}
                                    sx={{
                                        '& .MuiListItemText-primary': {
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: 'calc(100% - 10px)',
                                            paddingRight: '5px'
                                        }
                                    }}
                                />
                                <Box sx={{ position: 'absolute', right: 3, top: '50%', transform: 'translateY(-50%)', minWidth: '40px' }}>
                                    <IconButton
                                        className="chat-menu-icon"
                                        onClick={(e) => handleMenuOpen(e, conversation.id)}
                                        sx={{
                                            opacity: 0,
                                            transition: 'opacity 0.2s',
                                            color: conversation.id === selectedId ? '#fff' : 'inherit',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                            }
                                        }}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                </Box>
                            </ListItemButton>
                        )
                    })}
                </List>
            </Box>

            {/* Pulsante Nuova Chat fissato in basso */}
            <Box sx={{
                flexShrink: 0,
                width: '100%',
                padding: '0.5rem 0',
                position: 'sticky',
                bottom: isDesktop ? 75 : 0,
                backgroundColor: 'white',
            }}>
                <Button
                    variant='contained'
                    sx={{
                        backgroundColor: '#E9495A',
                        color: 'white',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        '&:hover': { backgroundColor: '#E6263B' }
                    }}
                    onClick={onNewChat}
                >
                    <AddIcon />
                    {t('common:new_chat')}
                </Button>
            </Box>

            {/* Menu contestuale per le chat */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <MenuItem onClick={handleEditClick}>
                    <EditIcon />
                </MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ color: '#d32f2f' }}>
                    <DeleteIcon />
                </MenuItem>
            </Menu>



            {/* Dialog per modificare il titolo della chat */}
            <Dialog open={editDialogOpen} onClose={() => {
                setEditDialogOpen(false);
                setEditTitle('');
                setSelectedChatId(null);
            }} maxWidth="sm" fullWidth>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        placeholder={t('common:edit')}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleEditConfirm();
                            }
                        }}
                        sx={{
                            margin: 0,
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: '#E0E0E0',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#E0E0E0',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#E0E0E0',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: '#000000',
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: '#000000',
                            },
                            '& .MuiInputBase-input.Mui-disabled': {
                                color: '#000000',
                            },
                            '& .MuiFormHelperText-root': {
                                textAlign: 'right',
                                marginRight: '5px',
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions className='w-full flex justify-center items-center'>
                    <Button
                        onClick={() => {
                            setEditDialogOpen(false);
                            setEditTitle('');
                            setSelectedChatId(null);
                        }}
                        sx={{ margin: '1rem', fontSize: { xs: '0.875rem', sm: '1rem' }, backgroundColor: 'trasparent', color: '#475467', border: '1px solid #667085' }}
                        fullWidth
                    >
                        {t('common:cancel')}
                    </Button>
                    <Button
                        onClick={handleEditConfirm}
                        variant="contained"
                        sx={{ margin: '1rem', fontSize: { xs: '0.875rem', sm: '1rem' }, backgroundColor: '#E9495A', color: '#FFFFFF', '&:hover': { backgroundColor: '#E6263B' } }}
                        fullWidth
                    >
                        {t('common:save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Drawer>
    );
};

export default Sidebar;
